import { useEffect, useState } from "react";
import { PlusCircle } from "lucide-react";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function fmtDollars(cents) {
    return ((cents || 0) / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function CreatePayoutModal({ open, onClose, onCreated }) {
    const [affiliates, setAffiliates] = useState([]);
    const [affiliateId, setAffiliateId] = useState("");
    const [pendingCommissions, setPendingCommissions] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [method, setMethod] = useState("paypal");
    const [reference, setReference] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!open) return;
        api.adminListAffiliates({ approved: "true" }).then((r) => setAffiliates(r.affiliates));
    }, [open]);

    useEffect(() => {
        if (!affiliateId) { setPendingCommissions([]); return; }
        api.adminListCommissions({ affiliate_id: affiliateId, status: "pending", limit: 500 }).then((r) => {
            setPendingCommissions(r.commissions);
            setSelectedIds(new Set(r.commissions.map((c) => c.id)));
            const aff = affiliates.find((a) => a.id === affiliateId);
            if (aff?.payout_method) setMethod(aff.payout_method);
        });
    }, [affiliateId, affiliates]);

    const currentAff = affiliates.find((a) => a.id === affiliateId);
    const payoutReady = !!(currentAff?.payout_method && currentAff?.payout_handle);

    const total = pendingCommissions.filter((c) => selectedIds.has(c.id)).reduce((s, c) => s + c.commission_amount_cents, 0);

    async function submit() {
        setBusy(true); setErr(null);
        try {
            await api.adminCreatePayout({
                affiliate_id: affiliateId,
                commission_ids: Array.from(selectedIds),
                method,
                reference: reference || null,
            });
            onCreated?.();
            onClose();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-lg" data-testid="new-payout-dialog">
                <DialogHeader>
                    <DialogTitle>Record a payout</DialogTitle>
                    <DialogDescription>
                        Marks the selected pending commissions as paid. This does not send money — it only records that money has left your bank.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div>
                        <label className="st-label">Affiliate</label>
                        <select className="st-input" value={affiliateId} onChange={(e) => setAffiliateId(e.target.value)}
                                data-testid="payout-affiliate">
                            <option value="">Select affiliate…</option>
                            {affiliates.map((a) => (
                                <option key={a.id} value={a.id}>{a.user.name} ({a.user.email}) — {fmtDollars(a.stats_lite.pending_cents)} pending</option>
                            ))}
                        </select>
                    </div>

                    {affiliateId && (
                        <div className="border rounded p-3 max-h-60 overflow-y-auto space-y-1"
                             style={{ borderColor: "var(--st-line)", background: "var(--st-silver-50)" }}
                             data-testid="payout-commissions-list">
                            {pendingCommissions.length === 0 ? (
                                <p className="text-sm italic" style={{ color: "var(--st-silver-500)" }}>
                                    No pending commissions for this affiliate.
                                </p>
                            ) : (
                                pendingCommissions.map((c) => (
                                    <label key={c.id} className="flex items-center justify-between gap-3 py-1.5 text-sm cursor-pointer">
                                        <span className="flex items-center gap-2">
                                            <input type="checkbox" checked={selectedIds.has(c.id)}
                                                   onChange={(e) => {
                                                       const s = new Set(selectedIds);
                                                       if (e.target.checked) s.add(c.id); else s.delete(c.id);
                                                       setSelectedIds(s);
                                                   }}
                                                   data-testid={`payout-commission-checkbox-${c.id}`} />
                                            <span className="text-xs" style={{ color: "var(--st-ink-muted)" }}>{new Date(c.earned_at).toLocaleDateString()}</span>
                                        </span>
                                        <span className="font-semibold" style={{ color: "var(--st-navy)" }}>{fmtDollars(c.commission_amount_cents)}</span>
                                    </label>
                                ))
                            )}
                        </div>
                    )}

                    {currentAff && !payoutReady && (
                        <div className="rounded p-3 text-xs" style={{ background: "#FFFAEB", color: "#7A2E0E", border: "1px solid #FEDF89" }} data-testid="modal-payout-missing">
                            <strong>Affiliate has not set a payout method + handle.</strong> Ask them to configure it from their dashboard, or add it via the affiliate detail page.
                        </div>
                    )}
                    {currentAff && payoutReady && (
                        <div className="rounded p-3 text-xs flex items-center justify-between gap-2"
                             style={{ background: "var(--st-silver-50)", border: "1px solid var(--st-line)" }}
                             data-testid="modal-payout-inline">
                            <div>
                                Sending to <strong style={{ color: "var(--st-navy)" }}>{currentAff.payout_method}</strong>
                                {" · "}
                                <span className="font-mono">{currentAff.payout_handle}</span>
                            </div>
                            <button type="button" className="link-royal text-xs"
                                    onClick={() => navigator.clipboard.writeText(currentAff.payout_handle)}
                                    data-testid="modal-copy-handle">Copy handle</button>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="st-label">Method</label>
                            <select className="st-input" value={method} onChange={(e) => setMethod(e.target.value)}
                                    data-testid="payout-method">
                                <option value="paypal">PayPal</option>
                                <option value="zelle">Zelle</option>
                                <option value="cashapp">Cash App</option>
                                <option value="venmo">Venmo</option>
                            </select>
                        </div>
                        <div>
                            <label className="st-label">Reference / tx id</label>
                            <input className="st-input" value={reference} onChange={(e) => setReference(e.target.value)}
                                   placeholder="Optional"
                                   data-testid="payout-reference" />
                        </div>
                    </div>

                    <div className="text-right text-sm" style={{ color: "var(--st-ink-muted)" }}>
                        Total: <span className="font-serif text-xl" style={{ color: "var(--st-navy)" }} data-testid="payout-total">{fmtDollars(total)}</span>
                    </div>

                    {err && <div className="text-sm" style={{ color: "#B42318" }}>{err}</div>}
                </div>

                <DialogFooter>
                    <button type="button" className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
                    <button type="button" className="btn-royal text-sm"
                            disabled={busy || !affiliateId || selectedIds.size === 0 || !payoutReady}
                            title={!payoutReady ? "Affiliate has not set a payout method + handle" : ""}
                            onClick={submit}
                            data-testid="payout-confirm">
                        {busy ? "Recording…" : "Record payout"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function AdminPayouts() {
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const r = await api.adminListPayouts({ limit: 200 });
            setPayouts(r.payouts);
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    return (
        <section className="max-w-6xl mx-auto px-6 py-10 space-y-6" data-testid="admin-payouts-page">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Payouts</div>
                    <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Affiliate payouts</h1>
                </div>
                <button type="button" className="btn-royal text-sm inline-flex items-center gap-2"
                        onClick={() => setDialogOpen(true)}
                        data-testid="new-payout-btn">
                    <PlusCircle size={14} /> New payout
                </button>
            </div>

            <div className="st-surface overflow-hidden" data-testid="admin-payouts-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr style={{ background: "var(--st-silver-50)", color: "var(--st-ink-muted)" }}>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Paid</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Affiliate</th>
                            <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Total</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Method</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Reference</th>
                            <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">#</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--st-silver-500)" }}>Loading…</td></tr>}
                        {!loading && payouts.length === 0 && <tr><td colSpan={6} className="text-center py-8 italic" style={{ color: "var(--st-silver-500)" }}>No payouts yet.</td></tr>}
                        {payouts.map((p) => (
                            <tr key={p.id} style={{ borderTop: "1px solid var(--st-line)" }} data-testid={`payout-row-${p.id}`}>
                                <td className="px-4 py-3 text-xs">{new Date(p.paid_at).toLocaleDateString()}</td>
                                <td className="px-4 py-3">
                                    <div className="font-semibold" style={{ color: "var(--st-navy)" }}>{p.affiliate_summary?.name || "—"}</div>
                                    <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>{p.affiliate_summary?.email}</div>
                                </td>
                                <td className="px-4 py-3 text-right font-semibold" style={{ color: "var(--st-navy)" }}>{fmtDollars(p.total_amount_cents)}</td>
                                <td className="px-4 py-3">{p.method}</td>
                                <td className="px-4 py-3 text-xs" style={{ color: "var(--st-ink-muted)" }}>{p.reference || "—"}</td>
                                <td className="px-4 py-3 text-right">{p.commission_ids.length}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <CreatePayoutModal open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={load} />
        </section>
    );
}
